import React, {useState, useCallback, useEffect} from 'react'
import {useProductContext} from '@/context/ProductContext'
import {VariationAttributeType} from '@/types/product'
import {ProductFavoritesButton} from './productFavoritesButton'
import {Carousel, CarouselContent, CarouselItem} from '@/components/ui/carousel'
import {ProductBadges} from '../product'
import {isProduction} from '../../utils/common'
import {type CarouselApi} from '@/components/ui/carousel'
import useEmblaCarousel from 'embla-carousel-react'

export interface ProductImageCarouselProps {
    className?: string
}

export const ProductImageCarousel: React.FC<ProductImageCarouselProps> = () => {
    const {product, activeProduct} = useProductContext()
    const [api, setApi] = useState<CarouselApi>()
    const [activeIndex, setActiveIndex] = useState(0)
    const [thumbRef, thumbApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true
    })

    const onSelect = useCallback(() => {
        if (!api) return
        const index = api.selectedScrollSnap()
        setActiveIndex(index)
        // Scroll the thumbnail strip to keep the active thumb visible
        thumbApi?.scrollTo(index)
    }, [api, thumbApi])

    // Register the onSelect listener whenever api changes
    useEffect(() => {
        if (!api) return
        api.on('select', onSelect)
        return () => {
            api.off('select', onSelect)
        }
    }, [api, onSelect])

    if (!product) return null

    const {imageGroups, id, name, url, c_badging, c_secondBadging} = product

    const badges = [c_badging, c_secondBadging].filter(Boolean) || []

    let imageGroup: {
        images: {alt: string; title: string; link: string}[]
        viewType: string
        variationAttributes: VariationAttributeType[]
    } | null = null
    if (imageGroups && activeProduct) {
        const variationValues = activeProduct.variationValues
        imageGroup =
            imageGroups.find((group) =>
                group.variationAttributes?.some((attribute: VariationAttributeType) => {
                    const variationValue = variationValues?.[attribute.id]
                    return (
                        variationValue &&
                        attribute.values.some((value) => value.value === variationValue)
                    )
                })
            ) || null
    }

    if (!imageGroup && imageGroups) {
        imageGroup = imageGroups[0]
    }

    if (!imageGroup) {
        return (
            <a href={url}>
                <img className="rounded" alt={name} src={product?.image_url} />
            </a>
        )
    }

    const slides = [
        ...(imageGroup?.images ?? []).map((img) => ({type: 'image' as const, img})),
        ...(product.c_youtubeID ? [{type: 'video' as const}] : [])
    ]

    return (
        <div className="flex flex-col gap-3 w-[80%]" data-testid="productImageCarousel-wrapper">
            {/* Main carousel — no arrows here */}
            <Carousel
                setApi={setApi}
                opts={{loop: true, slidesToScroll: 1}}
                className="w-full"
                data-testid="productImageCarousel"
            >
                <CarouselContent
                    className="mx-auto"
                    data-testid="productImageCarousel-CarouselContent"
                >
                    {imageGroup?.images?.map(
                        (img: {alt: string; title: string; link: string}, imgIdx: number) => {
                            const imageUrl = isProduction
                                ? `${new URL(img.link).pathname}`
                                : img.link
                            return (
                                <CarouselItem
                                    key={`carousel-product-img-${id}-0-${imgIdx}`}
                                    slidesVisible="1"
                                    slidePadding="1"
                                    slidesVisibleMobile="1"
                                    slidePaddingMobile="1"
                                    className="relative"
                                    data-testid="productImageCarousel-CarouselItem"
                                >
                                    <ProductBadges
                                        badges={badges}
                                        productId={id}
                                        data-testid="productTileImage-ProductBadges"
                                    />
                                    <img src={imageUrl} alt={img.alt} title={img.title} />
                                    <ProductFavoritesButton data-testid="productImageCarousel-ProductFavoritesButton" />
                                </CarouselItem>
                            )
                        }
                    )}
                    {product.c_youtubeID && (
                        <CarouselItem
                            slidesVisible="1"
                            slidePadding="1"
                            slidesVisibleMobile="1"
                            slidePaddingMobile="1"
                            className="relative"
                            data-testid="productImageCarousel-CarouselItem"
                        >
                            <iframe
                                title={`${name ?? ''} Youtube Video`}
                                width="100%"
                                height="100%"
                                src={`https://www.youtube.com/embed/${product.c_youtubeID}`}
                                allow="accelerometer;"
                                allowFullScreen
                            ></iframe>
                        </CarouselItem>
                    )}
                </CarouselContent>
            </Carousel>
            <div
                className="flex flex-row items-center"
                data-testid="productImageCarousel-thumbnails"
            >
                <button
                    onClick={() => api?.scrollPrev()}
                    className="flex-shrink-0 flex items-center justify-center w-7 h-7 hover:bg-gray-100 transition-colors"
                    aria-label="Previous image"
                    data-testid="productImageCarousel-CarouselPrevious"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="w-6 h-6"
                    >
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                </button>

                <div ref={thumbRef} className="overflow-hidden flex-1 h-7">
                    <div className="flex flex-row gap-2 justify-center">
                        {slides.map((slide, idx) => {
                            const isActive = activeIndex === idx
                            if (slide.type === 'video') {
                                return (
                                    <button
                                        key={`thumb-video-${idx}`}
                                        onClick={() => {
                                            api?.scrollTo(idx)
                                            thumbApi?.scrollTo(idx)
                                        }}
                                        className={`flex-shrink-0 w-7 h-7 rounded border-2 overflow-hidden transition-all duration-150 flex items-center justify-center bg-gray-100 ${
                                            isActive
                                                ? 'border-black opacity-100'
                                                : 'border-transparent opacity-50 hover:opacity-80'
                                        }`}
                                        aria-label="Video thumbnail"
                                        data-testid="productImageCarousel-thumbnail-video"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            className="w-6 h-6 text-gray-600"
                                        >
                                            <path d="M8 5v14l11-7z" />
                                        </svg>
                                    </button>
                                )
                            }

                            const img = slide.img
                            const imageUrl = isProduction
                                ? `${new URL(img.link).pathname}`
                                : img.link

                            return (
                                <button
                                    key={`thumb-${id}-${idx}`}
                                    onClick={() => {
                                        api?.scrollTo(idx)
                                        thumbApi?.scrollTo(idx)
                                    }}
                                    className={`flex-shrink-0 w-7 h-7 rounded border-none overflow-hidden transition-all duration-150 ${
                                        isActive
                                            ? 'border-black opacity-100'
                                            : 'border-transparent opacity-50 hover:opacity-80'
                                    }`}
                                    aria-label={img.alt || `Product image ${idx + 1}`}
                                    data-testid="productImageCarousel-thumbnail"
                                >
                                    <img
                                        src={imageUrl}
                                        alt={img.alt}
                                        className="w-7 h-7 object-cover"
                                    />
                                </button>
                            )
                        })}
                    </div>
                </div>
                <button
                    onClick={() => api?.scrollNext()}
                    className="flex-shrink-0 flex items-center justify-center w-7 h-7"
                    aria-label="Next image"
                    data-testid="productImageCarousel-CarouselNext"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="w-6 h-6"
                    >
                        <path d="M9 18l6-6-6-6" />
                    </svg>
                </button>
            </div>
        </div>
    )
}
